import { ART_PX } from "../../intro/pixel_raster"

// Where everything on the character-creation page sits, in art pixels of the page (one art pixel is
// ART_PX logical pixels of the page, the login intro's grid).
//
// Two layouts, by how large the game's stage is on screen:
//  - Desktop: the page is the stage. The scene is laid out on the game's usual 800 x 460 stage: the
//    figure, the rail and the deck keep their rows there, a wider or taller screen adds scene round them,
//    the panel holds the left edge and the way on holds the bottom right.
//  - Phone: the stage is shown at about half desktop size, too small to read or tap, so the whole page is
//    drawn up to twice as large (pageScale) and laid out in that smaller page: the wordmark across the top,
//    a tabbed panel either side of the figure, and the rolls and the way on along the bottom.

export interface ArtRect {
    x: number
    y: number
    w: number
    h: number
}

export interface CreationLayout {
    phone: boolean
    /** The visible page, in art pixels (x and y can be negative on an expanded desktop screen). */
    view: ArtRect
    figX: number
    feetY: number
    railY: number
    deckY: number
    postX: number
    lanternX: number
    /** The lantern's bottom row; it hangs from the post's arm above it. */
    lanternBase: number
    /** The flame, where the light comes from. */
    light: { x: number; y: number }
    titleY: number
    /** Where the shrouds meet the rail. */
    shrouds: number[]
    /** Desktop: the one panel. Phone: the left of the two. */
    panel: ArtRect
    /** Phone only: the panel right of the figure. */
    rightPanel?: ArtRect
    /** Phone only: the roll buttons, in the bottom row. */
    rolls?: { look: ArtRect; outfit: ArtRect }
    cta: ArtRect
    /**
     * How far the intro's night is moved, in art pixels, so its horizon sits as far above the rail as it does
     * on desktop and the moon stays in view. Zero on desktop.
     */
    night: { dx: number; dy: number }
}

export const PANEL_W = 142
export const PANEL_H = 214
export const CTA_W = 74
export const CTA_H = 14

/** Below this many screen pixels per stage pixel, the screen is a phone's. */
export const PHONE_BELOW = 1.2
/** What the phone layout needs, in art pixels of the page. */
export const PHONE_PAGE = { w: 250, h: 128 }
/** The figure's column between the phone's two panels: the figure and the turn plates either side of it. */
const PHONE_COLUMN = 50
const PHONE_PANEL_TOP = 30
const PHONE_BOTTOM_ROW = 13

/** The desktop scene's rows, from the feet line. */
const RAIL_ABOVE_FEET = 30
const DECK_ABOVE_FEET = 16
const LANTERN_ABOVE_FEET = 44
/** Where the intro's horizon is (authored art row), and how far above the rail it shows. */
const HORIZON_ROW = 156
const HORIZON_ABOVE_RAIL = 26
/** The intro's moon, mirrored onto the right as the intro shows it (art column). */
const MOON_COLUMN = 336

export interface StageBounds {
    left: number
    right: number
    top: number
    bottom: number
}

/**
 * How much larger than the stage the page is drawn: 1 on desktop; on a phone, up to 2, as far as the phone
 * layout fits the screen.
 */
export const pageScale = (screenPerStage: number, bounds: StageBounds): number => {
    if (!(screenPerStage < PHONE_BELOW)) {
        return 1
    }
    const width = bounds.right - bounds.left
    const height = bounds.bottom - bounds.top
    return Math.max(1, Math.min(2, width / (PHONE_PAGE.w * ART_PX), height / (PHONE_PAGE.h * ART_PX)))
}

/** The page's bounds, in its own logical pixels, for a stage drawn `scale` times larger. */
export const pageBounds = (bounds: StageBounds, scale: number): StageBounds => ({
    left: bounds.left / scale,
    right: bounds.right / scale,
    top: bounds.top / scale,
    bottom: bounds.bottom / scale,
})

const viewOf = (bounds: StageBounds): ArtRect => {
    const x = Math.floor(bounds.left / ART_PX)
    const y = Math.floor(bounds.top / ART_PX)
    return { x, y, w: Math.ceil(bounds.right / ART_PX) - x, h: Math.ceil(bounds.bottom / ART_PX) - y }
}

/** The scene's rows and the lantern, from where the figure stands. */
const sceneAt = (figX: number, feetY: number, postX: number) => {
    const lanternX = postX + 6
    const lanternBase = feetY - LANTERN_ABOVE_FEET
    return {
        figX,
        feetY,
        railY: feetY - RAIL_ABOVE_FEET,
        deckY: feetY - DECK_ABOVE_FEET,
        postX,
        lanternX,
        lanternBase,
        light: { x: lanternX + 3.5, y: lanternBase - 6 },
        shrouds: [figX + 120, figX + 132, figX + 144],
    }
}

/** The layout for a page of these bounds: the phone's when `phone`, else the desktop's. */
export const creationLayout = (bounds: StageBounds, phone: boolean = false): CreationLayout => {
    const view = viewOf(bounds)
    const right = view.x + view.w
    const bottom = view.y + view.h
    if (!phone) {
        const figX = 236
        return {
            phone: false,
            view,
            ...sceneAt(figX, 212, figX - 36),
            titleY: 9,
            panel: { x: view.x + 10, y: view.y + Math.max(2, Math.floor((view.h - PANEL_H) / 2)), w: PANEL_W, h: PANEL_H },
            cta: { x: right - 28 - CTA_W, y: bottom - 19, w: CTA_W, h: CTA_H },
            night: { dx: 0, dy: 0 },
        }
    }

    const figX = view.x + Math.floor(view.w / 2)
    const rowY = bottom - PHONE_BOTTOM_ROW - 2
    const feetY = rowY - 2
    const panelW = Math.floor((view.w - PHONE_COLUMN - 8) / 2)
    const panelTop = view.y + PHONE_PANEL_TOP
    const panelH = rowY - 2 - panelTop
    const scene = sceneAt(figX, feetY, figX - 21)
    return {
        phone: true,
        view,
        ...scene,
        // The wordmark's glow starts above its letters; that glow may run off the top.
        titleY: view.y - 3,
        panel: { x: view.x + 4, y: panelTop, w: panelW, h: panelH },
        rightPanel: { x: right - 4 - panelW, y: panelTop, w: panelW, h: panelH },
        rolls: {
            look: { x: view.x + 4, y: rowY, w: 62, h: PHONE_BOTTOM_ROW },
            outfit: { x: right - 4 - 63, y: rowY, w: 63, h: PHONE_BOTTOM_ROW },
        },
        cta: { x: figX - Math.floor(CTA_W / 2), y: rowY, w: CTA_W, h: PHONE_BOTTOM_ROW },
        night: { dx: right - 40 - MOON_COLUMN, dy: scene.railY - HORIZON_ABOVE_RAIL - HORIZON_ROW },
    }
}

/** The box the figure, its shadow and its dissolve are drawn in: room for the tallest body and its shadow. */
export const figureBox = (layout: CreationLayout): ArtRect => ({
    x: layout.figX - 60,
    y: layout.feetY - 70,
    w: 120,
    h: 80,
})
